/**
 * Right-pane label pickers for one dimension. Per `feature-spec.md` §3.4.2:
 *
 *   - Labels are grouped by category, with the category name as a subheading.
 *   - Each label is a pill toggle. Selected = filled. Unselected = outlined.
 *   - Hover reveals the label's description, examples, and regulation reference.
 *   - "None apply" toggle:
 *       * Toggling ON clears any selected labels and marks the step satisfied.
 *       * Selecting a label when ON flips the toggle OFF (selection wins).
 *       * (Spec gap: previous selection NOT restored on toggle off — out of scope.)
 */
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../../ui/tooltip';
import { cn } from '../../../../../../lib/utils';
import type { TaxonomyDimension, TaxonomyLabel } from '../../../types/taxonomy';

interface Props {
  dimension: TaxonomyDimension;
  selected: string[];
  noneApply: boolean;
  onToggleLabel: (labelId: string) => void;
  onToggleNoneApply: () => void;
}

const pillBase =
  'rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-[background-color,color,border-color] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

const pillSelected =
  'border-primary bg-primary text-primary-foreground hover:bg-primary/90';

const pillUnselected =
  'border-border bg-card text-foreground hover:border-primary/40 hover:bg-accent';

const pillDisabled = 'opacity-50';

const LabelPill: React.FC<{
  label: TaxonomyLabel;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}> = ({ label, selected, disabled, onToggle }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-pressed={selected}
        data-testid={`label-pill-${label.id}`}
        className={cn(
          pillBase,
          selected ? pillSelected : pillUnselected,
          disabled && pillDisabled,
        )}
      >
        {label.name}
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs">
      <div className="space-y-1">
        {label.description && (
          <p className="text-[12px] leading-relaxed">{label.description}</p>
        )}
        {label.examples && label.examples.length > 0 && (
          <div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
              Examples
            </div>
            <ul className="ml-3 list-disc text-[11px] leading-relaxed">
              {label.examples.map((ex, i) => (
                <li key={i}>{ex}</li>
              ))}
            </ul>
          </div>
        )}
        {label.regulationReference && (
          <p className="mt-1 text-[10px] italic opacity-80">
            Ref: {label.regulationReference}
          </p>
        )}
      </div>
    </TooltipContent>
  </Tooltip>
);

const LabelSelectorPane: React.FC<Props> = ({
  dimension,
  selected,
  noneApply,
  onToggleLabel,
  onToggleNoneApply,
}) => {
  return (
    <div className="space-y-4" data-testid="label-selector-pane">
      <div>
        <h2 className="font-jakarta text-xl font-bold tracking-[-0.02em] text-foreground">
          {dimension.name}
        </h2>
        {dimension.description && (
          <p className="mt-1 max-w-3xl text-[12px] leading-relaxed text-muted-foreground">
            {dimension.description}
          </p>
        )}
      </div>

      <div className="space-y-4">
        {dimension.categories.map((category) => (
          <section key={category.id} data-testid={`label-category-${category.id}`}>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {category.name}
            </h3>
            <div className="flex flex-wrap gap-2">
              {category.labels.map((label) => (
                <LabelPill
                  key={label.id}
                  label={label}
                  selected={selected.includes(label.id)}
                  onToggle={() => onToggleLabel(label.id)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="border-t border-border pt-4">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground">
          <input
            type="checkbox"
            checked={noneApply}
            onChange={onToggleNoneApply}
            data-testid="none-apply-toggle"
            className="h-4 w-4 cursor-pointer rounded border-border"
          />
          <span>None apply to this dimension</span>
        </label>
        <p className="ml-6 mt-1 text-[11px] text-muted-foreground">
          Marks the step satisfied with no labels. Selecting a label clears this toggle.
        </p>
      </div>
    </div>
  );
};

export default LabelSelectorPane;
